import type { FC } from 'react';
import { Fragment } from 'react';
import Icon from '@components/Icon';
import IconMdi from '@components/IconMdi';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { mdiMinus, mdiPlus, mdiTrashCan } from '@mdi/js';
import { resolveSchema, ROOT_SECTIONS_KEY } from '@root/helpers/schema';
import type { JSONSchema } from '@root/helpers/schema/types';
import mapChildren from '@root/src/mapChildren';
import { cloneDeep, get } from 'lodash-es';

import Button from '../../Button';
import Card from '../../Card';
import ErrorText from '../../ErrorText';
import CollapsibleValidatedItem from '../CollapsibleValidatedItem/CollapsibleValidatedItem';

import type { SectionRepeaterViewProps } from './types';

export const SectionRepeaterCollapsibleView: FC<SectionRepeaterViewProps> = ({
  autoGrow,
  canAdd,
  className,
  emptyContent,
  handleAdd,
  handleRemove,
  helperText,
  id,
  initialItems,
  isDisabled,
  isReadOnly = false,
  item,
  onChange,
  ownKey,
  schemaItem,
  showQuestionKey,
  startCollapsed = false,
  title,
  description,
  total100Error,
  validationErrors,
  hideErrors,
  isRequired,
}) => {
  const formValues = useDatapointsStore.getState().formValues;
  const isEditableOnly = get(item, 'ui:component:repeater:read-only', false);
  const isSections = ownKey === ROOT_SECTIONS_KEY;
  return (
    <CollapsibleValidatedItem
      className={className}
      id={id}
      title={title}
      ownKey={ownKey}
      helperText={helperText}
      startCollapsed={startCollapsed}
      isRequired={isRequired}
    >
      {emptyContent && (
        <div className="bg-info-50 text-info-600 mt-2 mb-4 flex items-center gap-x-3.5 rounded-md px-3 py-4 text-xs font-medium">
          <Icon name="book" />
          {emptyContent}
        </div>
      )}
      {description && (
        <div className="bg-info-50 text-info-600 mt-2 mb-4 flex items-center gap-x-3.5 rounded-md px-3 py-4 text-xs font-medium">
          <Icon name="info" className="h-4 w-4" />
          {description}
        </div>
      )}
      {initialItems.map((_lineItem, index) => {
        const key = `${ownKey}[${index}]`;
        const clonedSchema = cloneDeep(schemaItem);

        resolveSchema(clonedSchema, get(formValues, key));

        return (
          <Fragment key={key}>
            <Card
              className="mb-8"
              header={
                <Card.Header className="flex items-center justify-between p-0 pb-4">
                  <div className="font-medium">
                    {item.items?.title} #{index + 1}
                  </div>
                  <Show when={!isEditableOnly && !isReadOnly && isSections}>
                    <div className="flex items-center justify-end">
                      <Show when={canAdd}>
                        <Tooltip content="Add new above">
                          <Button
                            onClick={() => handleAdd(index)}
                            kind="primary"
                            className="h-7 p-1"
                            isDisabled={isDisabled}
                          >
                            <IconMdi path={mdiPlus} />
                          </Button>
                        </Tooltip>
                      </Show>
                      <Show
                        when={
                          (item?.minimum === undefined || item?.minimum < initialItems.length) &&
                          initialItems.length > 0
                        }
                      >
                        <Tooltip content="Remove">
                          <Button
                            className="bg-info-500 hover:!bg-info-600 ml-2 h-7 border-none p-1 font-normal text-white"
                            data-testid={`${id}-remove-section-button`}
                            onClick={() => handleRemove(index)}
                            isDisabled={isDisabled}
                          >
                            <IconMdi path={mdiTrashCan} />
                          </Button>
                        </Tooltip>
                      </Show>
                    </div>
                  </Show>
                </Card.Header>
              }
            >
              {mapChildren({
                autoGrow,
                onChange,
                parentKey: key,
                parentSchema: clonedSchema as JSONSchema,
                validationErrors,
                showQuestionKey,
                isReadOnly,
                isDisabled,
                hideErrors,
              })}
            </Card>
          </Fragment>
        );
      })}
      {total100Error && <ErrorText className="mb-4">{total100Error.message}</ErrorText>}
      <div className="flex">
        <Show
          when={
            !isSections &&
            !isEditableOnly &&
            !isReadOnly &&
            (item?.minimum === undefined || item?.minimum < initialItems.length) &&
            initialItems.length > 0
          }
        >
          <Button
            className="mr-4 h-10"
            kind="danger"
            data-testid={`${id}-remove-section-button`}
            onClick={() => handleRemove(initialItems.length - 1)}
            isDisabled={isDisabled}
          >
            <IconMdi path={mdiMinus} />
            {`Remove ${item.title ?? 'a section'}`}
          </Button>
        </Show>
        <Show when={!isEditableOnly && !isReadOnly && canAdd}>
          <Button
            onClick={() => handleAdd(initialItems.length)}
            kind="primary"
            className="h-10"
            isDisabled={isDisabled}
          >
            <IconMdi path={mdiPlus} />
            {`Add ${item.title ?? 'a section'}`}
          </Button>
        </Show>
      </div>
    </CollapsibleValidatedItem>
  );
};
